import React, { useEffect } from 'react';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { Header } from "@/components/Header";
import { FooterSection } from "@/components/FooterSection";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AnimatedCard from "@/components/AnimatedCard";
import { Button } from "@/components/Button";
import { Users } from "lucide-react";

const WorkshopsPricing = () => {
  useScrollToTop();

  useEffect(() => {
    document.title = "Team Workshop Pricing | DocketLabs";
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Team AI workshops for law firms. Expert-led training sessions, customized content & practical applications. Book your workshop.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Team AI workshops for law firms. Expert-led training sessions, customized content & practical applications. Book your workshop.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);

  return (
    <>
      <Header />
      <main className="pt-24 bg-background min-h-screen">
        <section className="px-6 py-16">
          <div className="container mx-auto max-w-5xl">
            <AnimatedCard animation="fade-in">
              <div className="text-center mb-12">
                <h1 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
                  <span className="text-gradient">Team Workshop</span>{" "}
                  <span className="text-white">Pricing</span>
                </h1>
                
                <p className="text-lg text-foreground/80 max-w-3xl mx-auto">
                  Empower your entire team with practical AI knowledge through our expert-led virtual workshops.
                </p>
              </div>
            </AnimatedCard>
            
            <AnimatedCard animation="fade-in" delay={200}>
              <div className="overflow-hidden rounded-xl border border-white/10 backdrop-blur-sm">
                <Table className="w-full">
                  <TableHeader className="bg-white/5">
                    <TableRow className="border-white/10">
                      <TableHead className="text-white font-semibold w-1/4">Plan</TableHead>
                      <TableHead className="text-white font-semibold w-1/3">What's Included</TableHead>
                      <TableHead className="text-white font-semibold w-1/4">Price</TableHead>
                      <TableHead className="text-white font-semibold w-1/4"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="border-white/10 hover:bg-white/5">
                      <TableCell className="font-medium text-white">Single Workshop</TableCell>
                      <TableCell className="text-white/70">One-hour virtual seminar, up to 25 participants</TableCell>
                      <TableCell className="text-white">$979</TableCell>
                      <TableCell>
                        <a href="https://buy.stripe.com/8x24gyb7F0Tlcdka4c6Vq03" target="_blank" rel="noopener noreferrer" className="w-full">
                          <Button variant="outline" className="w-full border-white/20 hover:bg-white/10">
                            Book One Workshop
                          </Button>
                        </a>
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-white/10 hover:bg-white/5 bg-primary/5">
                      <TableCell className="font-medium text-white">
                        <div className="flex flex-col">
                          <span>2-Workshop Bundle</span>
                          <span className="text-xs text-primary font-normal">(Most Popular)</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-white/70">Two customized one-hour seminars</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="line-through text-white/40">$1,958</div>
                          <div className="font-semibold text-white">$1,664</div>
                          <div className="text-xs text-primary">save 15%</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <a href="https://buy.stripe.com/8x2dR8dfNdG73GO1xG6Vq04" target="_blank" rel="noopener noreferrer" className="w-full">
                          <Button variant="primary" className="w-full" hasAnimation={true}>
                            Book 2-Workshop Bundle
                          </Button>
                        </a>
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-white/10 hover:bg-white/5">
                      <TableCell className="font-medium text-white">
                        <div className="flex flex-col">
                          <span>3-Workshop Bundle</span>
                          <span className="text-xs text-amber-400 font-normal">(Best Value)</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-white/70">Three customized one-hour seminars</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="line-through text-white/40">$2,937</div>
                          <div className="font-semibold text-white">$2,349</div>
                          <div className="text-xs text-amber-400">save 20%</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <a href="https://buy.stripe.com/8x24gy7Vt45x1yG0tC6Vq05" target="_blank" rel="noopener noreferrer" className="w-full">
                          <Button variant="outline" className="w-full border-white/20 hover:bg-white/10">
                            Book 3-Workshop Bundle
                          </Button>
                        </a>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </AnimatedCard>
            
            <AnimatedCard animation="fade-in" delay={300}>
              <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-lg">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-full bg-primary/20">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Workshop Details</h3>
                  </div>
                  
                  <ul className="space-y-3 text-foreground/80">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Interactive virtual sessions via Zoom</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Customized content based on your team's needs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Includes Q&A time for specific questions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Recording provided for team members who couldn't attend</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Resource materials provided after the session</span>
                    </li>
                  </ul>
                </div>
                
                <div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-lg">
                  <h3 className="text-xl font-semibold mb-4 text-white">Popular Workshop Topics</h3>
                  
                  <ul className="space-y-3 text-foreground/80">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>AI Ethics for Legal Teams</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Generative AI Tools for Legal Research</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Document Automation with AI</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>AI for Client Intake and Management</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>E-Discovery and AI: Best Practices</span>
                    </li>
                  </ul>
                </div>
              </div>
            </AnimatedCard>
          </div>
        </section>
      </main>
      <FooterSection />
    </>
  );
};

export default WorkshopsPricing;
